import { forwardRef } from 'react';
import { Field, mergeClasses } from '@fluentui/react-components';
import { DatePicker as FluentDatePicker } from '@fluentui/react-datepicker-compat';
import type { DatePickerProps as FluentDatePickerProps } from '@fluentui/react-datepicker-compat';
import { formatDate } from '@utils/dateFormat';
import { useDatePickerStyles } from './DatePicker.styles';

/**
 * Adapter that conforms to Fluent's `(date?: Date | null) => string` contract
 * while delegating to the central `formatDate` utility.
 */
const formatDateForDefaultRegion = (date?: Date | null): string => formatDate(date);

/**
 * Props for GEM DatePicker component.
 * Extends native Fluent UI DatePicker compat props.
 */
export interface DatePickerProps extends FluentDatePickerProps {
  /** Label text displayed above the datepicker */
  label?: string;
  /** Whether the field is required (shows asterisk) */
  required?: boolean;
  /** Validation message displayed below the datepicker */
  validationMessage?: string;
  /** Validation state controlling message styling */
  validationState?: 'error' | 'warning' | 'success' | 'none';
}

/**
 * Themed DatePicker — thin wrapper around @fluentui/react-datepicker-compat DatePicker + Field.
 * Applies GEM defaults: appearance="filled-lighter", size="large", and the shared regional date format.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      label,
      required,
      validationMessage,
      validationState,
      formatDate = formatDateForDefaultRegion,
      appearance = 'outline',
      size = 'large',
      positioning = {
        position: 'below',
        align: 'start',
        flipBoundary: 'scrollParent',
        overflowBoundary: 'scrollParent',
      },
      className,
      ...rest
    },
    ref
  ) => {
    const styles = useDatePickerStyles();

    return (
      <Field
        className={mergeClasses(styles.root, className)}
        label={label}
        required={required}
        validationMessage={validationMessage}
        validationState={validationState}
      >
        <FluentDatePicker
          ref={ref}
          appearance={appearance}
          size={size}
          formatDate={formatDate}
          positioning={positioning}
          inlinePopup
          {...rest}
        />
      </Field>
    );
  }
);

DatePicker.displayName = 'DatePicker';
